import React, { createContext, useState, useEffect, useContext } from "react";
import { Bounce, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { db } from "../services/firebase";
import { useAuth } from "./AuthContext";
import { doc, setDoc, getDoc, deleteDoc } from "firebase/firestore";

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
  const { currentUser } = useAuth();

  const [wishlistItems, setWishlistItems] = useState(() => {
    if (typeof window !== "undefined") {
      const savedWishlistItems = localStorage.getItem("wishlistItems");
      return savedWishlistItems ? JSON.parse(savedWishlistItems) : [];
    }
    return [];
  });

  useEffect(() => {
    if (currentUser) {
      const fetchWishlistItems = async () => {
        try {
          const wishlistRef = doc(db, "wishlists", currentUser.uid);
          const wishlistDoc = await getDoc(wishlistRef);
          if (wishlistDoc.exists()) {
            setWishlistItems(wishlistDoc.data().items || []);
          }
        } catch (error) {
          console.error("Error fetching wishlists:", error);
        }
      };

      fetchWishlistItems();
    }
  }, [currentUser]);

  useEffect(() => {
    const saveWishlistToDatabase = async (items) => {
      if (currentUser) {
        try {
          const wishlistRef = doc(db, "wishlists", currentUser.uid);
          await setDoc(wishlistRef, { items });
          // console.log("Wishlist saved to database:", items);
        } catch (error) {
          console.error("Error saving wishlist to database:", error);
        }
      } else {
        localStorage.setItem("wishlistItems", JSON.stringify(items));
        // console.log("Saving wishlist to localStorage:", items);
      }
    };

    const timeoutId = setTimeout(() => {
      saveWishlistToDatabase(wishlistItems);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [wishlistItems, currentUser]);

  const notifyAdd = () =>
    toast.success("Product Added to Wishlist", {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
    });

  // ADDED: isInWishlist function
  const isInWishlist = (productId) => {
    return wishlistItems.some((item) => item.id === productId);
  };

  const addToWishlist = (product) => {
    setWishlistItems((prevItems) => {
      const existingProduct = prevItems.find((item) => item.id === product.id);
      if (existingProduct) {
        return prevItems;
      } else {
        notifyAdd();
        return [...prevItems, product];
      }
    });
  };

  const removeFromWishlist = (productId) => {
    setWishlistItems((prevItems) =>
      prevItems.filter((item) => item.id !== productId),
    );
  };

  const getTotalWish = () => {
    return wishlistItems.length;
  };

  const clearWishlist = async () => {
    setWishlistItems([]);
    if (currentUser) {
      try {
        await deleteDoc(doc(db, "wishlists", currentUser.uid));
      } catch (error) {
        console.error("Error clearing wishlist from database:", error);
      }
    } else {
      localStorage.removeItem("wishlistItems");
    }
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlistItems,
        addToWishlist,
        removeFromWishlist,
        clearWishlist,
        getTotalWish,
        isInWishlist, // ADDED: Export the isInWishlist function
      }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
